import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Award, Beaker, Package, HeartHandshake, Sparkles,
  Factory, FlaskConical, Trophy, Users2, Rocket, Leaf, Star, Check,
  MapPin, Phone, Mail, Clock, Send, Eye, Target, Heart, MessageCircle, Play,
} from "lucide-react";
import { WA, trackEvent, api, resolveMediaUrl, formatApiError, getYoutubeEmbedUrl, getYoutubeVideoId, getYoutubeThumbnail, getYoutubeWatchUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/lib/images";
import { bi, emptyBi } from "@/lib/bilingual";
import { matchGalleryToServices } from "@/lib/galleryMatch";
import { Reveal, Overline } from "@/components/site/SectionHeader";
import DraggableMarquee from "@/components/site/DraggableMarquee";
import { useCachedGet } from "@/hooks/useCachedGet";

const serviceIcons = [FlaskConical, Package, Factory, Beaker, Leaf, HeartHandshake];
const advantageIcons = [Trophy, ShieldCheck, Sparkles, Rocket, Users2, Award];
const valueIcons = [Eye, Target, Heart];

const SectionRenderer = ({ section }) => {
  const p = section.props || {};
  switch (section.type) {
    case "hero": return <HeroSection {...p} />;
    case "page_hero": return <PageHeroSection {...p} />;
    case "home_hero": return <HomeHeroSection {...p} />;
    case "rich_text": return <RichTextSection {...p} />;
    case "image": return <ImageSection {...p} />;
    case "image_text": return <ImageTextSection {...p} />;
    case "cta": return <CTASection {...p} />;
    case "two_column": return <TwoColumnSection {...p} />;
    case "cards_grid": return <CardsGridSection {...p} />;
    case "process_steps": return <ProcessStepsSection {...p} />;
    case "certs_marquee": return <CertsMarqueeSection {...p} />;
    case "certs_grid": return <CertsGridSection {...p} />;
    case "services_list": return <ServicesListSection {...p} />;
    case "products_feed": return <ProductsFeedSection {...p} />;
    case "articles_feed": return <ArticlesFeedSection {...p} />;
    case "gallery_feed": return <GalleryFeedSection {...p} />;
    case "faq_feed": return <FaqFeedSection {...p} />;
    case "testimonials_feed": return <TestimonialsFeedSection {...p} />;
    case "clients_marquee": return <ClientsMarqueeSection {...p} />;
    case "contact_block": return <ContactBlockSection {...p} />;
    case "gallery": return <GallerySection {...p} />;
    case "spacer": return <div style={{ height: p.height || 60 }} />;
    default: return null;
  }
};

export const SECTION_TYPES = [
  { type: "home_hero", label: "Hero Beranda", defaults: {
    badge: emptyBi("Dari Ide Produk Hingga Siap Dipasarkan", "From Product Idea to Market Ready"),
    titleBefore: emptyBi("Jasa ", "Trusted "),
    titleHighlight: emptyBi("Maklon Minuman Serbuk", "Powder Drink Manufacturing"),
    titleAfter: emptyBi(" Terpercaya di Indonesia", " Partner in Indonesia"),
    lead: emptyBi("PT Mandala Cahaya Sentosa membantu Anda mewujudkan brand minuman serbuk sendiri.", "PT Mandala Cahaya Sentosa helps you build your own powder drink brand."),
    ctaPrimary: emptyBi("Konsultasi Gratis Sekarang", "Free Consultation Now"),
    ctaSecondary: emptyBi("Lihat Produk Kami", "See Our Products"),
    ctaSecondaryLink: "/produk",
    bgImage: IMAGES.factoryHero,
    sideImage: "/hero-products.png",
    floatHalal: emptyBi("Bersertifikat Halal & BPOM", "Halal & BPOM Certified"),
    floatRating: emptyBi("Rating Pelanggan 4.9 / 5.0", "Customer Rating 4.9 / 5.0"),
    showStats: true,
  }},
  { type: "page_hero", label: "Hero Halaman", defaults: {
    overline: emptyBi("Overline", "Overline"),
    title: emptyBi("Judul Halaman", "Page Title"),
    lead: emptyBi("Lead singkat.", "Short lead."),
    bgImage: "",
  }},
  { type: "hero", label: "Hero Banner (klasik)", defaults: {
    overline: emptyBi("Overline", "Overline"),
    title: emptyBi("Judul Utama", "Main Title"),
    subtitle: emptyBi("Subjudul singkat.", "Short subtitle."),
    ctaText: emptyBi("Konsultasi Gratis", "Free Consultation"),
    ctaLink: "#wa",
    bgImage: IMAGES.factoryHero,
  }},
  { type: "image_text", label: "Gambar + Teks", defaults: {
    overline: emptyBi("Cerita", "Story"),
    title: emptyBi("Judul", "Title"),
    body: emptyBi("Paragraf konten...", "Content paragraph..."),
    image: "/mcs-welcome.jpg",
    imageAlt: emptyBi("Gambar", "Image"),
    videoUrl: "",
    flip: false,
  }},
  { type: "rich_text", label: "Rich Text", defaults: { content: emptyBi("<h2>Judul</h2><p>Konten...</p>", "<h2>Title</h2><p>Content...</p>") } },
  { type: "image", label: "Gambar Tunggal", defaults: { url: IMAGES.coffee, alt: emptyBi("", ""), caption: emptyBi("", ""), fullwidth: false } },
  { type: "cta", label: "Call to Action", defaults: {
    title: emptyBi("Siap Berkolaborasi?", "Ready to Collaborate?"),
    subtitle: emptyBi("Diskusikan produk Anda dengan tim kami.", "Discuss your product with our team."),
    primaryText: emptyBi("Chat WhatsApp", "Chat WhatsApp"),
    primaryLink: "#wa",
    secondaryText: emptyBi("Kontak", "Contact"),
    secondaryLink: "/kontak",
  }},
  { type: "two_column", label: "Dua Kolom", defaults: {
    leftTitle: emptyBi("Kolom Kiri", "Left Column"),
    leftContent: emptyBi("Konten kiri...", "Left content..."),
    rightImage: IMAGES.lab,
  }},
  { type: "cards_grid", label: "Grid Kartu", defaults: {
    overline: emptyBi("Layanan", "Services"),
    title: emptyBi("Kartu Grid", "Card Grid"),
    subtitle: emptyBi("", ""),
    columns: 3,
    iconSet: "services",
    cards: [
      { title: emptyBi("Kartu 1", "Card 1"), desc: emptyBi("Deskripsi.", "Description."), image: "" },
      { title: emptyBi("Kartu 2", "Card 2"), desc: emptyBi("Deskripsi.", "Description."), image: "" },
      { title: emptyBi("Kartu 3", "Card 3"), desc: emptyBi("Deskripsi.", "Description."), image: "" },
    ],
  }},
  { type: "process_steps", label: "Langkah Proses", defaults: {
    overline: emptyBi("Alur Proses", "Process"),
    title: emptyBi("5 Langkah", "5 Steps"),
    subtitle: emptyBi("", ""),
    steps: [
      { title: emptyBi("Langkah 1", "Step 1"), desc: emptyBi("Deskripsi", "Description") },
      { title: emptyBi("Langkah 2", "Step 2"), desc: emptyBi("Deskripsi", "Description") },
    ],
  }},
  { type: "certs_marquee", label: "Marquee Sertifikasi", defaults: {
    overline: emptyBi("Tersertifikasi", "Certified"),
    items: ["BPOM", "HALAL MUI", "GMP", "HACCP", "ISO 9001", "ISO 22000", "CPPOB"],
  }},
  { type: "certs_grid", label: "Grid Sertifikasi", defaults: {
    overline: emptyBi("Sertifikasi", "Certifications"),
    title: emptyBi("Standar Kualitas", "Quality Standards"),
    subtitle: emptyBi("", ""),
    items: [
      { code: "BPOM", desc: emptyBi("Badan Pengawas Obat & Makanan", "Indonesian FDA") },
      { code: "HALAL", desc: emptyBi("MUI Indonesia", "MUI Indonesia") },
    ],
  }},
  { type: "services_list", label: "Daftar Layanan", defaults: {
    items: [
      {
        title: emptyBi("Maklon Minuman Serbuk", "Powder Drink Manufacturing"),
        desc: emptyBi("Deskripsi layanan.", "Service description."),
        bullets: [emptyBi("Bullet 1", "Bullet 1")],
        image: "",
        galleryId: "",
      },
    ],
  }},
  { type: "products_feed", label: "Feed Produk", defaults: {
    overline: emptyBi("Produk", "Products"),
    title: emptyBi("Produk Unggulan", "Featured Products"),
    subtitle: emptyBi("", ""),
    limit: 0,
    showFilters: true,
    ctaText: emptyBi("Lihat Semua Produk", "See All Products"),
    ctaLink: "/produk",
  }},
  { type: "articles_feed", label: "Feed Artikel", defaults: {
    overline: emptyBi("Artikel", "Articles"),
    title: emptyBi("Artikel & Insights", "Articles & Insights"),
    subtitle: emptyBi("", ""),
  }},
  { type: "gallery_feed", label: "Feed Galeri", defaults: {
    overline: emptyBi("Galeri", "Gallery"),
    title: emptyBi("Galeri", "Gallery"),
    subtitle: emptyBi("", ""),
  }},
  { type: "faq_feed", label: "Feed FAQ", defaults: {
    overline: emptyBi("FAQ", "FAQ"),
    title: emptyBi("Pertanyaan Umum", "FAQ"),
    subtitle: emptyBi("", ""),
    stillTitle: emptyBi("Masih punya pertanyaan?", "Still have questions?"),
    stillBody: emptyBi("Tim kami siap membantu.", "Our team is ready to help."),
  }},
  { type: "testimonials_feed", label: "Feed Testimoni", defaults: {
    overline: emptyBi("Testimoni", "Testimonials"),
    title: emptyBi("Dipercaya Brand", "Trusted by Brands"),
  }},
  { type: "clients_marquee", label: "Marquee Logo Klien", defaults: {
    overline: emptyBi("Klien Maklon", "Our Clients"),
    title: emptyBi("Dipercaya Brand di Seluruh Indonesia", "Trusted by Brands Across Indonesia"),
  }},
  { type: "contact_block", label: "Blok Kontak", defaults: {
    infoTitle: emptyBi("Informasi Kontak", "Contact Info"),
    infoLead: emptyBi("Terhubung dengan tim kami.", "Connect with our team."),
    formTitle: emptyBi("Kirim Pesan", "Send Message"),
  }},
  { type: "gallery", label: "Galeri Foto (statis)", defaults: { images: [IMAGES.coffee, IMAGES.collagen, IMAGES.herbal] } },
  { type: "spacer", label: "Spacer", defaults: { height: 60 } },
];

// ---------- helpers ----------
const useLang = () => {
  const { lang } = useLanguage();
  return lang || "id";
};

const img = (url) => resolveMediaUrl(url) || url;

// ---------- Section Components ----------

const HomeHeroSection = (p) => {
  const lang = useLang();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const wa = WA(settings?.whatsapp_number, settings?.whatsapp_template);
  const stats = settings?.stats || {};
  const sideImage = p.sideImage || "/hero-products-clean.png?v=1";
  const floatHalal = bi(p.floatHalal, lang) || (lang === "en" ? "Halal & BPOM Certified" : "Bersertifikat Halal & BPOM");
  const floatRating = bi(p.floatRating, lang) || (lang === "en" ? "Customer Rating 4.9 / 5.0" : "Rating Pelanggan 4.9 / 5.0");
  const titleBefore = (bi(p.titleBefore, lang) || "").trim();
  const titleHighlight = (bi(p.titleHighlight, lang) || "").trim();
  const titleAfter = (bi(p.titleAfter, lang) || "").trim();
  const subtitle = bi(p.badge, lang) || t("home.heroSubtitle");

  const checks = [
    t("home.statSince"),
    `${stats.brands_served || "500+"} ${t("home.statBrands")}`,
    `${stats.formulas_created || "1000+"} ${t("home.statFormulas")}`,
    t("home.statLegal"),
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-[88px] lg:pt-24 pb-10 lg:pb-16 bg-[#fff5f5]"
    >
      <div className="absolute -left-24 top-20 w-72 h-72 rounded-full bg-maroon-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -right-16 bottom-0 w-80 h-80 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 container-wide">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="max-w-xl xl:max-w-2xl">
            <h1 className="text-[1.85rem] sm:text-4xl lg:text-[2.65rem] xl:text-[2.9rem] font-extrabold tracking-tight leading-[1.25] font-display text-zinc-900">
              {titleBefore ? <span>{titleBefore}</span> : null}
              {titleHighlight ? (
                <span className="text-maroon-800">
                  {titleBefore ? " " : ""}
                  {titleHighlight}
                </span>
              ) : null}
              {titleAfter ? (
                <span>
                  {titleBefore || titleHighlight ? " " : ""}
                  {titleAfter}
                </span>
              ) : null}
            </h1>

            {subtitle ? (
              <h2 className="mt-3 text-lg sm:text-xl lg:text-2xl font-bold text-maroon-800 leading-snug">
                {subtitle}
              </h2>
            ) : null}

            {bi(p.lead, lang) && (
              <p className="mt-5 text-[0.95rem] sm:text-base text-zinc-600 leading-relaxed">
                {bi(p.lead, lang)}
              </p>
            )}

            {p.showStats !== false && (
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {checks.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm sm:text-[15px] font-semibold text-zinc-800">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-maroon-800 text-white flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-wa-btn"
                onClick={() => trackEvent("click", pathname, "whatsapp")}
                className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-maroon-800 text-white font-bold hover:bg-maroon-900 transition-colors shadow-[0_8px_20px_rgba(232,32,32,0.28)]"
              >
                {bi(p.ctaPrimary, lang) || t("nav.consult")}
              </a>
              <Link
                to={p.ctaSecondaryLink || "/produk"}
                data-testid="hero-produk-btn"
                className="inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full border-2 border-maroon-800 text-maroon-800 font-bold hover:bg-maroon-800 hover:text-white transition-colors"
              >
                {bi(p.ctaSecondary, lang)}
              </Link>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[420px] sm:max-w-[520px] lg:max-w-none">
              <img
                src={img(sideImage)}
                alt="Produk maklon Mandala"
                className="relative z-[1] w-full h-auto object-contain hero-float select-none bg-transparent drop-shadow-[0_25px_40px_rgba(80,0,20,0.12)]"
                draggable={false}
              />
              <div className="absolute z-[2] left-0 sm:left-2 top-4 sm:top-8 flex items-center gap-2 rounded-2xl bg-white px-3 py-2 shadow-[0_12px_30px_rgba(26,39,68,0.12)]">
                <span className="w-8 h-8 rounded-full bg-maroon-800 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck size={15} />
                </span>
                <span className="text-[11px] sm:text-sm font-semibold text-[#1a2744] leading-snug max-w-[10rem]">
                  {floatHalal}
                </span>
              </div>
              <div className="absolute z-[2] right-0 sm:right-2 bottom-6 flex items-center gap-2 rounded-2xl bg-white px-3 py-2 shadow-[0_12px_30px_rgba(26,39,68,0.12)]">
                <span className="w-8 h-8 rounded-full bg-maroon-800 text-white flex items-center justify-center shrink-0">
                  <Star size={15} fill="currentColor" />
                </span>
                <span className="text-[11px] sm:text-sm font-semibold text-[#1a2744] leading-snug max-w-[10rem]">
                  {floatRating}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PageHeroSection = ({ overline, title, lead, bgImage }) => {
  const lang = useLang();
  return (
    <section className="pt-12 lg:pt-16 pb-10 md:pb-14 bg-[#fff5f5] relative overflow-hidden border-b border-[rgba(232,32,32,0.16)]">
      {bgImage && (
        <div className="absolute inset-0 z-0 opacity-15">
          <img src={img(bgImage)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="container-wide relative">
        {bi(overline, lang) && (
          <div className="text-sm font-bold text-maroon-800">{bi(overline, lang)}</div>
        )}
        {bi(title, lang) && (
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold text-zinc-900 tracking-tight text-balance max-w-4xl">
            {bi(title, lang)}
          </h1>
        )}
        {bi(lead, lang) && (
          <p className="mt-4 max-w-3xl text-zinc-600 text-base sm:text-lg leading-relaxed">{bi(lead, lang)}</p>
        )}
      </div>
    </section>
  );
};

const HeroSection = ({ overline, title, subtitle, ctaText, ctaLink, bgImage, height = 480 }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const link = ctaLink === "#wa" ? WA(settings?.whatsapp_number, settings?.whatsapp_template) : ctaLink;
  const isWa = ctaLink === "#wa";
  return (
    <section className="relative flex items-center overflow-hidden" style={{ minHeight: height }}>
      {bgImage && (
        <div className="absolute inset-0 z-0">
          <img src={img(bgImage)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 gradient-maroon opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-zinc-950/20 to-transparent" />
        </div>
      )}
      <div className="container-wide relative z-10 py-16 text-white">
        {bi(overline, lang) && <div className="text-xs uppercase tracking-[0.22em] text-maroon-200 font-bold">{bi(overline, lang)}</div>}
        {bi(title, lang) && <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter text-balance leading-tight max-w-3xl">{bi(title, lang)}</h1>}
        {bi(subtitle, lang) && <p className="mt-5 text-base sm:text-lg text-zinc-200 max-w-2xl leading-relaxed">{bi(subtitle, lang)}</p>}
        {bi(ctaText, lang) && (
          <a href={link} target={isWa ? "_blank" : "_self"} rel="noopener noreferrer"
            onClick={() => isWa && trackEvent("click", pathname, "whatsapp")}
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-maroon-900 font-semibold hover:-translate-y-0.5 transition shadow-xl">
            {bi(ctaText, lang)} <ArrowRight size={16} />
          </a>
        )}
      </div>
    </section>
  );
};

const RichTextSection = ({ content }) => {
  const lang = useLang();
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container-wide max-w-3xl">
        <div className="prose prose-zinc max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-maroon-700 prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: bi(content, lang) || "" }} />
      </div>
    </section>
  );
};

const ImageSection = ({ url, alt, caption, fullwidth }) => {
  const lang = useLang();
  if (!url) return null;
  const inner = (
    <figure>
      <img src={img(url)} alt={bi(alt, lang) || ""} className={`w-full ${fullwidth ? "" : "rounded-3xl"}`} />
      {bi(caption, lang) && <figcaption className="text-center text-sm text-zinc-500 mt-3">{bi(caption, lang)}</figcaption>}
    </figure>
  );
  return (
    <section className="py-10">
      {fullwidth ? inner : <div className="container-wide max-w-5xl">{inner}</div>}
    </section>
  );
};

const ImageTextSection = ({ overline, title, body, image, imageAlt, videoUrl, flip }) => {
  const lang = useLang();
  const embed = getYoutubeEmbedUrl(videoUrl);
  const [playing, setPlaying] = useState(false);
  const thumb = img(image) || "/mcs-welcome.jpg";
  const mediaTitle = bi(imageAlt, lang) || bi(title, lang) || "Video YouTube";

  useEffect(() => {
    setPlaying(false);
  }, [videoUrl, image]);

  return (
    <section className="section-pad bg-white">
      <div
        className={`container-wide grid gap-12 lg:gap-14 items-center ${
          embed
            ? `lg:grid-cols-[1.2fr_1fr] ${flip ? "lg:[&>*:first-child]:order-2" : ""}`
            : `lg:grid-cols-2 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`
        }`}
      >
        <Reveal>
          {embed ? (
            <div className="relative w-full aspect-video rounded-3xl overflow-hidden bg-zinc-900 shadow-[0_20px_50px_rgba(0,0,0,0.12)]">
              {playing ? (
                <iframe
                  src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1`}
                  title={mediaTitle}
                  className="absolute inset-0 w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="group absolute inset-0 w-full h-full text-left"
                  aria-label="Putar video"
                >
                  <img
                    src={thumb}
                    alt={mediaTitle}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-maroon-950/35 group-hover:bg-maroon-950/45 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20">
                      <span className="absolute inset-0 rounded-full bg-white/45 video-play-ping" />
                      <span className="absolute inset-0 rounded-full bg-white/25 video-play-ping video-play-ping-delay" />
                      <span className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white text-maroon-800 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                        <Play size={28} className="ml-1" fill="currentColor" />
                      </span>
                    </span>
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              {image && <img src={img(image)} alt={bi(imageAlt, lang)} className="w-full h-full object-cover" loading="lazy" />}
            </div>
          )}
        </Reveal>
        <Reveal delay={0.1}>
          {bi(overline, lang) && <Overline>{bi(overline, lang)}</Overline>}
          {bi(title, lang) && (
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-zinc-900 tracking-tight text-balance">
              {bi(title, lang)}
            </h2>
          )}
          {bi(body, lang) && (
            <div className="mt-6 space-y-4 text-zinc-600 leading-relaxed whitespace-pre-line">{bi(body, lang)}</div>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const CTASection = ({ title, subtitle, primaryText, primaryLink, secondaryText, secondaryLink }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const pLink = primaryLink === "#wa" ? WA(settings?.whatsapp_number, settings?.whatsapp_template) : primaryLink;
  const isWa = primaryLink === "#wa";
  return (
    <section className="py-14 md:py-20">
      <div className="container-wide">
        <div className="rounded-[28px] bg-maroon-950 p-10 md:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <h3 className="relative text-3xl md:text-4xl font-display font-extrabold tracking-tight text-balance">{bi(title, lang)}</h3>
          {bi(subtitle, lang) && <p className="relative mt-4 text-white/85 max-w-2xl mx-auto leading-relaxed">{bi(subtitle, lang)}</p>}
          <div className="relative mt-8 flex flex-wrap gap-3 justify-center">
            {bi(primaryText, lang) && (
              <a href={pLink} target={isWa ? "_blank" : "_self"} rel="noopener noreferrer"
                onClick={() => isWa && trackEvent("click", pathname, "whatsapp")}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-maroon-800 font-bold shadow-[0_8px_20px_rgba(18,50,77,0.18)]">
                {bi(primaryText, lang)} <ArrowRight size={16} />
              </a>
            )}
            {bi(secondaryText, lang) && (
              <Link to={secondaryLink || "#"} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/40 text-white font-semibold hover:bg-white/20">
                {bi(secondaryText, lang)}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const TwoColumnSection = ({ leftTitle, leftContent, rightTitle, rightContent, leftImage, rightImage }) => {
  const lang = useLang();
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container-wide grid lg:grid-cols-2 gap-12 items-center">
        <div>
          {leftImage && <img src={img(leftImage)} alt="" className="rounded-3xl mb-6 w-full" />}
          {bi(leftTitle, lang) && <h2 className="text-3xl md:text-4xl font-display font-bold text-zinc-900 tracking-tight">{bi(leftTitle, lang)}</h2>}
          {bi(leftContent, lang) && <div className="mt-4 text-zinc-600 leading-relaxed whitespace-pre-line">{bi(leftContent, lang)}</div>}
        </div>
        <div>
          {rightImage && <img src={img(rightImage)} alt="" className="rounded-3xl mb-6 w-full" />}
          {bi(rightTitle, lang) && <h2 className="text-3xl md:text-4xl font-display font-bold text-zinc-900 tracking-tight">{bi(rightTitle, lang)}</h2>}
          {bi(rightContent, lang) && <div className="mt-4 text-zinc-600 leading-relaxed whitespace-pre-line">{bi(rightContent, lang)}</div>}
        </div>
      </div>
    </section>
  );
};

const CardsGridSection = ({ overline, title, subtitle, cards = [], columns = 3, iconSet = "services", ctaText, ctaLink }) => {
  const lang = useLang();
  const icons = iconSet === "advantages" ? advantageIcons : iconSet === "values" ? valueIcons : serviceIcons;
  const grid = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[columns] || "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container-wide">
        <div className="max-w-3xl mx-auto text-center">
          {bi(overline, lang) && <div className="text-sm font-bold text-maroon-800">{bi(overline, lang)}</div>}
          {bi(title, lang) && <h2 className="mt-2 text-3xl md:text-4xl lg:text-[2.5rem] font-display font-extrabold text-zinc-900 tracking-tight text-balance">{bi(title, lang)}</h2>}
          {bi(subtitle, lang) && <p className="mt-4 text-zinc-600 leading-relaxed">{bi(subtitle, lang)}</p>}
        </div>
        <div className={`mt-12 grid gap-5 ${grid}`}>
          {cards.map((c, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={i} className="p-7 rounded-2xl bg-[#fbf8f4] border border-[#eee6d8] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(80,0,20,0.08)] transition">
                {c.image ? (
                  <img src={img(c.image)} alt="" className="w-14 h-14 rounded-xl object-cover mb-4" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-maroon-800 text-white flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                )}
                <h4 className="font-display font-bold text-lg text-zinc-900">{bi(c.title, lang)}</h4>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{bi(c.desc, lang)}</p>
              </div>
            );
          })}
        </div>
        {bi(ctaText, lang) && ctaLink && (
          <div className="mt-10 text-center">
            <Link to={ctaLink} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-maroon-800 text-maroon-800 font-bold hover:bg-maroon-800 hover:text-white transition">
              {bi(ctaText, lang)} <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const ProcessStepsSection = ({ overline, title, subtitle, steps = [] }) => {
  const lang = useLang();
  return (
    <section className="py-16 md:py-24 bg-[#fff5f5]">
      <div className="container-wide">
        <div className="max-w-3xl mx-auto text-center">
          {bi(overline, lang) && <div className="text-sm font-bold text-maroon-800">{bi(overline, lang)}</div>}
          {bi(title, lang) && <h2 className="mt-2 text-3xl md:text-4xl font-display font-extrabold text-zinc-900 tracking-tight">{bi(title, lang)}</h2>}
          {bi(subtitle, lang) && <p className="mt-4 text-zinc-600">{bi(subtitle, lang)}</p>}
        </div>
        <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {steps.map((s, i) => (
            <div key={i} className="relative rounded-2xl bg-white p-6 shadow-[0_8px_24px_rgba(80,0,20,0.05)]">
              <div className="w-10 h-10 rounded-full bg-maroon-800 text-white flex items-center justify-center font-display font-black">
                {i + 1}
              </div>
              <h4 className="mt-4 font-display font-bold text-zinc-900">{bi(s.title || s.t, lang)}</h4>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{bi(s.desc || s.d, lang)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CertsMarqueeSection = ({ overline, items = [] }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const logos = (settings?.certifications || []).filter((c) => c?.image);
  const fallback = (items.length ? items : ["BPOM", "HALAL", "GMP"]).map((c, i) => ({
    id: `text-${i}`,
    name: typeof c === "string" ? c : bi(c, lang),
    image: "",
  }));
  const list = logos.length ? logos : fallback;

  return (
    <section className="py-10 bg-white border-y border-zinc-100">
      <div className="container-wide mb-4">
        {bi(overline, lang) && (
          <div className="text-sm font-bold text-maroon-800 text-center">
            {bi(overline, lang)}
          </div>
        )}
      </div>
      <DraggableMarquee
        gradient
        gradientColor="white"
        gradientWidth={80}
        speed={40}
      >
        {list.map((c) =>
          c.image ? (
            <div
              key={c.id}
              className="mx-4 sm:mx-6 h-16 sm:h-20 flex items-center justify-center"
              title={c.name || ""}
            >
              <img
                src={resolveMediaUrl(c.image)}
                alt={c.name || "Sertifikasi"}
                className="h-12 sm:h-16 w-auto max-w-[140px] object-contain pointer-events-none"
                loading="lazy"
                draggable={false}
              />
            </div>
          ) : (
            <span
              key={c.id}
              className="mx-6 text-lg font-display font-bold text-zinc-400 tracking-widest uppercase"
            >
              {c.name}
            </span>
          )
        )}
      </DraggableMarquee>
    </section>
  );
};

const CertsGridSection = ({ overline, title, subtitle, items = [] }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const logos = (settings?.certifications || []).filter((c) => c?.image);

  return (
    <section className="py-16 md:py-24 bg-zinc-50">
      <div className="container-wide">
        {bi(overline, lang) && (
          <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">
            {bi(overline, lang)}
          </div>
        )}
        {bi(title, lang) && (
          <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold text-zinc-900">
            {bi(title, lang)}
          </h2>
        )}
        {bi(subtitle, lang) && (
          <p className="mt-4 text-zinc-600 max-w-2xl">{bi(subtitle, lang)}</p>
        )}

        {logos.length > 0 ? (
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {logos.map((c) => (
              <div
                key={c.id}
                className="rounded-2xl bg-white border border-zinc-200 p-5 md:p-6 flex flex-col items-center justify-center gap-3 min-h-[140px] md:min-h-[160px]"
                title={c.name || ""}
              >
                <img
                  src={resolveMediaUrl(c.image)}
                  alt={c.name || "Sertifikasi"}
                  className="h-16 md:h-20 w-auto max-w-full object-contain"
                  loading="lazy"
                />
                {c.name ? (
                  <div className="text-sm font-semibold text-zinc-700 text-center">{c.name}</div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-zinc-200 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-maroon-50 text-maroon-800 flex items-center justify-center shrink-0">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-zinc-900">{item.code || item.c}</div>
                  <div className="text-sm text-zinc-600 mt-1">{bi(item.desc || item.d, lang)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const ServicesListSection = ({ items = [] }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const wa = WA(settings?.whatsapp_number, settings?.whatsapp_template);
  const { data: galleryRaw } = useCachedGet("/gallery");
  const gallery = Array.isArray(galleryRaw) ? galleryRaw : [];
  const matched = useMemo(() => matchGalleryToServices(items, gallery), [items, gallery]);
  const photoOf = (s, i) => matched[i]?.image || s.image;
  return (
    <section className="section-pad bg-white">
      <div className="container-wide space-y-24">
        {items.map((s, i) => {
          const Icon = serviceIcons[i % serviceIcons.length];
          const photo = photoOf(s, i);
          return (
            <Reveal key={i}>
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-zinc-50">
                  {photo && <img src={img(photo)} alt={bi(s.title, lang)} className="w-full h-full object-contain p-4" />}
                </div>
                <div>
                  <div className="w-14 h-14 rounded-xl bg-maroon-50 text-maroon-800 flex items-center justify-center">
                    <Icon size={24} />
                  </div>
                  <h3 className="mt-5 text-2xl md:text-3xl font-display font-bold text-zinc-900">{bi(s.title, lang)}</h3>
                  <p className="mt-3 text-zinc-600 leading-relaxed">{bi(s.desc, lang)}</p>
                  {(s.bullets || []).length > 0 && (
                    <ul className="mt-5 space-y-2">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-zinc-700">
                          <Check size={16} className="text-maroon-700 mt-0.5 shrink-0" /> {bi(b, lang)}
                        </li>
                      ))}
                    </ul>
                  )}
                  <a href={wa} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackEvent("click", pathname, "whatsapp")}
                    className="mt-6 inline-flex items-center gap-2 text-maroon-800 font-semibold">
                    {t("common.chatWhatsApp")} <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const catKey = (c) => {
  if (c && typeof c === "object") return String(c.id || c.en || "");
  return String(c || "");
};

const ProductsFeedSection = ({ overline, title, subtitle, limit = 0, showFilters = true, ctaText, ctaLink }) => {
  const lang = useLang();
  const { t } = useLanguage();
  const { data: productsRaw, ready } = useCachedGet("/products", { published: true });
  const products = Array.isArray(productsRaw) ? productsRaw : [];
  const [filter, setFilter] = useState("__all__");
  const catMap = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[catKey(p.category)] = p.category; });
    return map;
  }, [products]);
  const categories = useMemo(
    () => ["__all__", ...Object.keys(catMap).filter(Boolean)],
    [catMap]
  );
  let list = filter === "__all__" ? products : products.filter((p) => catKey(p.category) === filter);
  if (limit > 0) list = list.slice(0, limit);
  return (
    <section className="section-pad bg-white">
      <div className="container-wide">
        {(bi(overline, lang) || bi(title, lang)) && (
          <div className="mb-10">
            {bi(overline, lang) && <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{bi(overline, lang)}</div>}
            {bi(title, lang) && <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold text-zinc-900">{bi(title, lang)}</h2>}
            {bi(subtitle, lang) && <p className="mt-3 text-zinc-600 max-w-2xl">{bi(subtitle, lang)}</p>}
          </div>
        )}
        {showFilters && categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button key={c} onClick={() => setFilter(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition ${filter === c ? "bg-maroon-800 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
                {c === "__all__" ? t("common.filterAll") : bi(catMap[c], lang)}
              </button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[8rem]">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <Link to={`/produk/${p.slug}`} className="group block">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-white relative">
                  <img src={resolveMediaUrl(p.image)} alt={bi(p.name, lang)} className="w-full h-full object-contain p-4" loading="lazy" />
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-maroon-800">{bi(p.category, lang)}</div>
                </div>
                <div className="pt-5">
                  <h3 className="text-lg font-display font-bold text-zinc-900 group-hover:text-maroon-800 transition">{bi(p.name, lang)}</h3>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed line-clamp-2">{bi(p.description, lang)}</p>
                  <div className="mt-4 flex items-center gap-2 text-maroon-800 font-semibold text-sm">
                    {t("products.detail")} <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
          {ready && list.length === 0 && <div className="col-span-full text-center py-16 text-zinc-500">{t("products.empty")}</div>}
        </div>
        {bi(ctaText, lang) && ctaLink && (
          <div className="mt-10 text-center">
            <Link to={ctaLink} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-maroon-800 text-white font-semibold">
              {bi(ctaText, lang)} <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const ArticlesFeedSection = ({ overline, title, subtitle }) => {
  const lang = useLang();
  const { t } = useLanguage();
  const { data: itemsRaw, ready } = useCachedGet("/articles", { published: true });
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  return (
    <section className="section-pad bg-white">
      <div className="container-wide">
        {(bi(overline, lang) || bi(title, lang)) && (
          <div className="mb-10">
            {bi(overline, lang) && <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{bi(overline, lang)}</div>}
            {bi(title, lang) && <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold text-zinc-900">{bi(title, lang)}</h2>}
            {bi(subtitle, lang) && <p className="mt-3 text-zinc-600 max-w-2xl">{bi(subtitle, lang)}</p>}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[8rem]">
          {items.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.05}>
              <Link to={`/artikel/${a.slug}`} className="group block">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-zinc-100">
                  <img src={resolveMediaUrl(a.thumbnail)} alt={bi(a.title, lang)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                </div>
                <div className="pt-4">
                  {bi(a.category, lang) && <div className="text-xs font-semibold text-maroon-700 uppercase tracking-wider">{bi(a.category, lang)}</div>}
                  <h3 className="mt-2 text-lg font-display font-bold text-zinc-900 group-hover:text-maroon-800">{bi(a.title, lang)}</h3>
                  <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{bi(a.excerpt, lang)}</p>
                </div>
              </Link>
            </Reveal>
          ))}
          {ready && items.length === 0 && <div className="col-span-full text-center py-16 text-zinc-500">{t("articles.empty")}</div>}
        </div>
      </div>
    </section>
  );
};

const GALLERY_ALBUMS = ["Staff", "Rnd", "Produksi", "Event"];
const galleryAlbumKey = (a) => String(a || "").toLowerCase().replace(/&/g, "").replace(/\s+/g, "");

const GalleryYoutubeCard = ({ item }) => {
  const [playing, setPlaying] = useState(false);
  const source = item.link || item.image;
  const videoId = getYoutubeVideoId(source);
  const embed = videoId
    ? getYoutubeEmbedUrl(source, { autoplay: playing })
    : null;
  const thumb = getYoutubeThumbnail(source) || resolveMediaUrl(item.image);
  const watchUrl = getYoutubeWatchUrl(source);

  if (!embed || !videoId) return null;

  return (
    <div className="rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-100 shadow-sm">
      <div className="aspect-video relative bg-black">
        {playing ? (
          <iframe
            key={`yt-${videoId}-play`}
            title={item.title || "YouTube"}
            src={embed}
            className="absolute inset-0 w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full group text-left cursor-pointer"
            aria-label={`Putar ${item.title || "video YouTube"}`}
          >
            {thumb ? (
              <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="absolute inset-0 bg-zinc-800" />
            )}
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-[#E82020] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          </button>
        )}
      </div>
      <div className="px-4 py-3 bg-white flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-zinc-900 line-clamp-2">{item.title || "YouTube"}</div>
        {watchUrl && (
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-xs font-semibold text-maroon-800 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            YouTube
          </a>
        )}
      </div>
    </div>
  );
};

const GalleryFeedSection = ({ overline, title, subtitle }) => {
  const lang = useLang();
  const { t } = useLanguage();
  const { data: itemsRaw, ready } = useCachedGet("/gallery");
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  const [album, setAlbum] = useState("__all__");
  const albums = useMemo(() => {
    const found = Array.from(new Set(items.map((g) => g.album).filter(Boolean)));
    const keys = new Map();
    found.forEach((f) => {
      const k = galleryAlbumKey(f);
      if (!keys.has(k)) {
        const pref = GALLERY_ALBUMS.find((p) => galleryAlbumKey(p) === k);
        keys.set(k, pref || f);
      }
    });
    const ordered = [
      ...GALLERY_ALBUMS.filter((a) => keys.has(galleryAlbumKey(a))),
      ...Array.from(keys.values()).filter((f) => !GALLERY_ALBUMS.some((p) => galleryAlbumKey(p) === galleryAlbumKey(f))),
    ];
    return ["__all__", ...ordered];
  }, [items]);
  const filtered = album === "__all__"
    ? items
    : items.filter((g) => galleryAlbumKey(g.album) === galleryAlbumKey(album));

  return (
    <section className="section-pad bg-white">
      <div className="container-wide">
        {(bi(overline, lang) || bi(title, lang)) && (
          <div className="mb-10">
            {bi(overline, lang) && <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{bi(overline, lang)}</div>}
            {bi(title, lang) && <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold text-zinc-900">{bi(title, lang)}</h2>}
            {bi(subtitle, lang) && <p className="mt-3 text-zinc-600 max-w-2xl">{bi(subtitle, lang)}</p>}
          </div>
        )}
        {albums.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {albums.map((a) => (
              <button key={a} onClick={() => setAlbum(a)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold ${album === a ? "bg-maroon-800 text-white" : "bg-zinc-100 text-zinc-700"}`}>
                {a === "__all__" ? t("common.filterAll") : a}
              </button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 min-h-[8rem]">
          {filtered.map((g) => {
            const isYoutube = !!(getYoutubeVideoId(g.link) || getYoutubeVideoId(g.image));
            if (isYoutube) {
              return (
                <div key={g.id} className="sm:col-span-2 lg:col-span-1">
                  <GalleryYoutubeCard item={g} />
                </div>
              );
            }
            const openUrl = g.link || "";
            const media = (
              <div className="aspect-square rounded-2xl overflow-hidden bg-zinc-100 relative group">
                <img
                  src={resolveMediaUrl(g.image)}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  loading="lazy"
                />
              </div>
            );
            return openUrl ? (
              <a key={g.id} href={openUrl} target="_blank" rel="noopener noreferrer">{media}</a>
            ) : (
              <div key={g.id}>{media}</div>
            );
          })}
          {ready && filtered.length === 0 && <div className="col-span-full text-center py-16 text-zinc-500">{t("gallery.empty")}</div>}
        </div>
      </div>
    </section>
  );
};

const FaqFeedSection = ({ overline, title, subtitle, stillTitle, stillBody }) => {
  const lang = useLang();
  const { t } = useLanguage();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const { data: itemsRaw, ready } = useCachedGet("/faqs", { published: true });
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  const [open, setOpen] = useState(null);
  const wa = WA(settings?.whatsapp_number, settings?.whatsapp_template);
  return (
    <section className="section-pad bg-white">
      <div className="container-wide max-w-3xl">
        {(bi(overline, lang) || bi(title, lang)) && (
          <div className="mb-10 text-center">
            {bi(overline, lang) && <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{bi(overline, lang)}</div>}
            {bi(title, lang) && <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold text-zinc-900">{bi(title, lang)}</h2>}
            {bi(subtitle, lang) && <p className="mt-3 text-zinc-600">{bi(subtitle, lang)}</p>}
          </div>
        )}
        <div className="space-y-3 min-h-[4rem]">
          {items.map((f) => (
            <div key={f.id} className="rounded-2xl border border-zinc-200 overflow-hidden">
              <button onClick={() => setOpen(open === f.id ? null : f.id)}
                className="w-full text-left px-5 py-4 font-semibold text-zinc-900 flex justify-between gap-4">
                {bi(f.question, lang) || f.question}
                <span className="text-maroon-700">{open === f.id ? "−" : "+"}</span>
              </button>
              {open === f.id && <div className="px-5 pb-4 text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{bi(f.answer, lang) || f.answer}</div>}
            </div>
          ))}
          {ready && items.length === 0 && <div className="text-center py-16 text-zinc-500">{t("faq.empty")}</div>}
        </div>
        {(bi(stillTitle, lang) || bi(stillBody, lang)) && (
          <div className="mt-12 p-8 rounded-3xl bg-maroon-50 text-center">
            <h3 className="text-xl font-display font-bold text-zinc-900">{bi(stillTitle, lang)}</h3>
            <p className="mt-2 text-zinc-600">{bi(stillBody, lang)}</p>
            <a href={wa} target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent("click", pathname, "whatsapp")}
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-maroon-800 text-white font-semibold">
              {t("common.chatWhatsApp")} <ArrowRight size={14} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

const TestimonialsFeedSection = ({ overline, title }) => {
  const lang = useLang();
  const { data: itemsRaw } = useCachedGet("/testimonials", { published: true });
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  if (!items.length) return null;
  return (
    <section className="py-16 md:py-24 bg-[#fff5f5]">
      <div className="container-wide">
        {bi(overline, lang) && <div className="text-sm font-bold text-maroon-800 text-center">{bi(overline, lang)}</div>}
        {bi(title, lang) && <h2 className="mt-2 text-3xl md:text-4xl font-display font-extrabold text-zinc-900 text-center">{bi(title, lang)}</h2>}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {items.map((tm) => (
            <div key={tm.id} className="p-7 rounded-2xl bg-white border border-zinc-200">
              <div className="flex gap-1 text-amber-400 mb-3">
                {Array.from({ length: tm.rating || 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <p className="text-zinc-700 leading-relaxed text-sm">"{tm.content}"</p>
              <div className="mt-5 font-semibold text-zinc-900">{tm.name}</div>
              {tm.position && <div className="text-xs text-zinc-500">{tm.position}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClientsMarqueeSection = ({ overline, title }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const logos = (settings?.client_logos || []).filter((c) => c?.image);
  if (!logos.length) return null;

  // Bagi dua baris agar slide berlawanan terasa padat
  const mid = Math.ceil(logos.length / 2);
  const rowRight = logos.slice(0, mid);
  const rowLeft = logos.slice(mid);
  // Jika sedikit, pakai semua di kedua baris
  const top = rowRight.length ? rowRight : logos;
  const bottom = rowLeft.length ? rowLeft : [...logos].reverse();

  const LogoItem = ({ c }) => (
    <div
      className="mx-4 sm:mx-6 h-16 sm:h-[4.5rem] w-[120px] sm:w-[140px] flex items-center justify-center shrink-0"
      title={c.name || ""}
    >
      <img
        src={resolveMediaUrl(c.image)}
        alt={c.name || "Klien"}
        className="max-h-12 sm:max-h-14 max-w-full object-contain opacity-80 hover:opacity-100 transition-opacity pointer-events-none"
        loading="lazy"
        draggable={false}
      />
    </div>
  );

  return (
    <section className="py-12 md:py-16 bg-white border-y border-zinc-100">
      <div className="container-wide mb-8">
        {bi(overline, lang) && (
          <div className="text-sm font-bold text-maroon-800 text-center">
            {bi(overline, lang)}
          </div>
        )}
        {bi(title, lang) && (
          <h2 className="mt-2 text-2xl md:text-3xl font-display font-extrabold text-zinc-900 text-center text-balance">
            {bi(title, lang)}
          </h2>
        )}
      </div>
      <div className="space-y-6">
        <DraggableMarquee
          direction="right"
          gradient
          gradientColor="white"
          gradientWidth={64}
          speed={35}
        >
          {top.map((c) => (
            <LogoItem key={`r-${c.id}`} c={c} />
          ))}
        </DraggableMarquee>
        <DraggableMarquee
          direction="left"
          gradient
          gradientColor="white"
          gradientWidth={64}
          speed={35}
        >
          {bottom.map((c) => (
            <LogoItem key={`l-${c.id}`} c={c} />
          ))}
        </DraggableMarquee>
      </div>
    </section>
  );
};

const ContactBlockSection = ({ infoTitle, infoLead, formTitle }) => {
  const lang = useLang();
  const { settings } = useSettings();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", product_interest: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      trackEvent("click", "/kontak", "form-submit");
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", product_interest: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("contact.errorSend"));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section className="section-pad bg-white">
      <div className="container-wide grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <h2 className="text-3xl font-display font-bold text-zinc-900">{bi(infoTitle, lang) || t("contact.infoTitle")}</h2>
          <p className="mt-3 text-zinc-600">{bi(infoLead, lang) || t("contact.infoLead")}</p>
          <div className="mt-8 space-y-5">
            {[
              { Icon: MapPin, label: t("contact.address"), value: settings?.address },
              { Icon: Phone, label: t("contact.phone"), value: settings?.phone },
              { Icon: Mail, label: t("contact.email"), value: settings?.email },
              { Icon: Clock, label: t("contact.hours"), value: settings?.operating_hours },
            ].map(({ Icon, label, value }, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-maroon-100 text-maroon-800 flex items-center justify-center shrink-0"><Icon size={18} /></div>
                <div>
                  <div className="font-semibold text-zinc-900">{label}</div>
                  <div className="text-sm text-zinc-600">{value}</div>
                </div>
              </div>
            ))}
          </div>
          {settings?.google_maps_embed && (
            <div className="mt-8 rounded-2xl overflow-hidden aspect-video bg-zinc-100">
              <iframe title="map" src={settings.google_maps_embed} className="w-full h-full border-0" loading="lazy" />
            </div>
          )}
        </div>
        <div className="lg:col-span-3">
          <h3 className="text-xl font-display font-bold text-zinc-900 mb-4">{bi(formTitle, lang) || t("common.sendMessage")}</h3>
          <form onSubmit={submit} className="space-y-4">
            <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("contact.name")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("contact.emailField")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("contact.whatsapp")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("contact.company")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <input value={form.product_interest} onChange={(e) => setForm({ ...form, product_interest: e.target.value })} placeholder={t("contact.productPlaceholder")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t("contact.message")}
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-maroon-800 text-white font-semibold disabled:opacity-60">
              <Send size={16} /> {submitting ? t("contact.sending") : t("common.sendMessage")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const GallerySection = ({ images = [] }) => (
  <section className="py-16 bg-white">
    <div className="container-wide">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((src, i) => (
          <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-zinc-100">
            <img src={img(src)} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-700" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default SectionRenderer;
